import _ from 'lodash'
import * as React from 'react'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'

import type { TenantEditDataType } from 'api/tenants'
import { Role } from 'api/users'

import { selectPostcodeStatus } from 'slices/postcodeSlice'
import { getTenant, selectTenantsStatus } from 'slices/tenantsSlice'
import { selectUsersStatus } from 'slices/usersSlice'

// 新規作成時のみtenantIdを指定しないのでオプショナル型にする
const useTenant = (tenantId?: number) => {
  const { tenant } = useSelector(selectTenantsStatus, shallowEqual)
  const { allTenantUsers } = useSelector(selectUsersStatus, shallowEqual)
  const { postcodeList } = useSelector(selectPostcodeStatus, shallowEqual)

  const dispatch = useDispatch()

  const [initEditData, setInitEditData] = React.useState<TenantEditDataType>({
    name: '',
    salesOfficeName: null,
    postalCode: '',
    prefecture: '',
    municipality: '',
    otherAddress: '',
    phoneNumber: '',
    personInChargeId: '',
    businessStartHour: '08',
    businessStartMinute: '00',
    businessEndHour: '18',
    businessEndMinute: '00',
  })
  const [editData, setEditData] = React.useState<TenantEditDataType>(initEditData)
  const [nameValidity, setNameValidity] = React.useState(false)
  const [phoneNumberValidity, setPhoneNumberValidity] = React.useState(false)
  const [postalCodeValidity, setPostalCodeValidity] = React.useState(false)
  const [prefectureValidity, setPrefectureValidity] = React.useState(false)
  const [municipalityValidity, setMunicipalityValidity] = React.useState(false)
  const [otherAddressValidity, setOtherAddressValidity] = React.useState(false)
  const [businessTimeValidity, setBusinessTimeValidity] = React.useState(false)
  const [personInChargeValidity, setPersonInChargeValidity] = React.useState(false)

  const disabled = React.useMemo(
    () =>
      !(
        nameValidity &&
        postalCodeValidity &&
        prefectureValidity &&
        municipalityValidity &&
        otherAddressValidity &&
        phoneNumberValidity &&
        businessTimeValidity &&
        personInChargeValidity
      ),
    [
      nameValidity,
      postalCodeValidity,
      prefectureValidity,
      municipalityValidity,
      otherAddressValidity,
      phoneNumberValidity,
      businessTimeValidity,
      personInChargeValidity,
    ]
  )

  React.useEffect(() => {
    if (!tenantId) {
      return
    }
    if (tenant) {
      const businessStartTime = tenant?.businessStartTime.split(':')
      const businessEndTime = tenant?.businessEndTime.split(':')
      const updatedData = {
        name: tenant.name,
        salesOfficeName: tenant.salesOfficeName,
        postalCode: tenant?.postalCode || '',
        prefecture: tenant?.prefecture || '',
        municipality: tenant?.municipality || '',
        otherAddress: tenant?.otherAddress || '',
        phoneNumber: tenant?.phoneNumber || '',
        personInChargeId: tenant?.personInChargeId || '',
        businessStartHour: businessStartTime?.[0] || '08',
        businessStartMinute: businessStartTime?.[1] || '00',
        businessEndHour: businessEndTime?.[0] || '18',
        businessEndMinute: businessEndTime?.[1] || '00',
      }
      setInitEditData(updatedData)
      setEditData(updatedData)
      return
    }
    dispatch(getTenant(tenantId))
  }, [tenantId, tenant, setEditData, setInitEditData, dispatch])

  const unchanged = React.useMemo(() => _.isEqual(editData, initEditData), [editData, initEditData])

  const targetPostalCode = React.useMemo(
    () => postcodeList.find(p => p.postcode === editData.postalCode),
    [editData.postalCode, postcodeList]
  )

  const personInChargeItems: { key: string; value: string }[] = React.useMemo(
    () =>
      allTenantUsers
        .filter(user => user.partialUserHasTenants[0].role === Role.TenantAdmin)
        .map(user => ({ key: user.userId, value: user.partialUserHasTenants[0]?.nickname ?? '' })),
    [allTenantUsers]
  )

  React.useEffect(() => {
    const start = editData.businessStartHour + editData.businessStartMinute
    const end = editData.businessEndHour + editData.businessEndMinute
    setBusinessTimeValidity(start < end || start + end === '')
  }, [editData.businessStartHour, editData.businessStartMinute, editData.businessEndHour, editData.businessEndMinute])

  return {
    disabled,
    unchanged,
    editData,
    initEditData,
    targetPostalCode,
    personInChargeItems,
    businessTimeValidity,
    personInChargeValidity,
    setEditData,
    setInitEditData,
    setNameValidity,
    setPhoneNumberValidity,
    setPostalCodeValidity,
    setPrefectureValidity,
    setMunicipalityValidity,
    setOtherAddressValidity,
    setBusinessTimeValidity,
    setPersonInChargeValidity,
  }
}

export default useTenant
